import {randImageUrl,randVideoUrl,slugify,embedUrlFor} from './random.js';
import {IMAGE_HOSTS,VIDEO_HOST} from './constants.js';
import {htmlAttrEscape,htmlTextEscape} from './escaping.js';
import {buildEvaluationHtml} from './evaluationArticle.js';

export const isStoryTemplate=(html)=> (
  html.includes('class="Story_card__') &&
  html.includes('class="Rail_list__') &&
  html.includes('id="__NEXT_DATA__"')
);

const replaceText=(html,pattern,value)=> html.replace(pattern,(_,open,close)=> (
  `${open}${htmlTextEscape(value)}${close}`
));

const replaceAttr=(html,pattern,value)=> html.replace(pattern,(_,open,close)=> (
  `${open}${htmlAttrEscape(value)}${close}`
));

const replaceJsonStrings=(value,replacer)=> {
  if (typeof value==='string') return replacer(value);
  if (Array.isArray(value)) return value.map((item)=> replaceJsonStrings(item,replacer));
  if (value && typeof value==='object') {
    return Object.fromEntries(
      Object.entries(value).map(([key,item])=> [key,replaceJsonStrings(item,replacer)])
    );
  }
  return value;
};

const campaignCopy=(value,keyword,product,llmJson)=> {
  if (!/mitolyn/i.test(value)) return value;
  if (/nutritional deficiencies|physiological support|internal organ|micronutrients|bio-available|botanical extracts|digestive harmony|daily immunity|cellular repair|metabolic balance|physical performance|supplement|non-gmo/i.test(value)) {
    return llmJson.meta_description || llmJson.standfirst || product;
  }
  return value.replace(/mitolyn/gi,product);
};

const rewriteStructuredData=(html,keyword,product,llmJson,canonical,videoUrl,imageUrl)=> {
  let videoIndex=0;
  return html.replace(
    /(<script type="application\/ld\+json">)([\s\S]*?)(<\/script>)/gi,
    (whole,open,body,close)=> {
      try {
        const data=JSON.parse(body);
        const updated=replaceJsonStrings(data,(text)=> campaignCopy(text,keyword,product,llmJson));
        const updateVideo=(video)=> {
          video.name=videoIndex===0
            ? (llmJson.seo_title || keyword)
            : (llmJson.card_titles?.[(videoIndex - 1) % 6] || product);
          video.description=videoIndex===0
            ? (llmJson.standfirst || llmJson.meta_description)
            : llmJson.card_contents?.[(videoIndex - 1) % 6] || llmJson.standfirst || llmJson.meta_description;
          video.thumbnailUrl=Array.isArray(video.thumbnailUrl) ? [imageUrl] : imageUrl;
          video.contentUrl=videoUrl;
          // Replaces the template's relative "/embed/<id>" (which validators
          // resolve against their own placeholder domain): the user's Video
          // URL base + the same random video ID as contentUrl.
          video.embedUrl=embedUrlFor(videoUrl);
          videoIndex++;
        };
        if (updated['@type']==='VideoObject') updateVideo(updated);
        if (updated['@type']==='WebPage') {
          updated.name=llmJson.seo_title || keyword;
          updated.url=canonical;   // the real address this page is published at
          updated.description=llmJson.json_ld_description || llmJson.meta_description;
          if (updated.primaryImageOfPage) updated.primaryImageOfPage.url=imageUrl;
          if (updated.mainEntity?.['@type']==='VideoObject') updateVideo(updated.mainEntity);
        }
        return open + JSON.stringify(updated) + close;
      } catch {
        return whole;
      }
    }
  );
};

export const transformStoryTemplate=(template,keyword,product,canonical,llmJson,rng,customImageBase,customVideoBase,scriptBase,scriptName=null)=> {
  const changes=[];
  let html=template;
  const imageUrl=randImageUrl(rng.choice(IMAGE_HOSTS),rng,customImageBase);
  // A real recording supplied by the owner replaces the generated video URL.
  const generatedVideoUrl=randVideoUrl(VIDEO_HOST,rng,customVideoBase);
  const videoUrl=llmJson.research?.evidence?.videoUrl || generatedVideoUrl;
  // The user's SEO title + random code, exactly as built. Never truncated.
  const title=(llmJson.seo_title || keyword).trim();
  const description=llmJson.meta_description || `${product} review`;
  const date=new Date().toLocaleDateString('en-US',{month: 'long',day: '2-digit',year: 'numeric'});

  html=replaceText(html,/(<title>)[\s\S]*?(<\/title>)/i,title);
  html=replaceText(html,/(<h1 class="Story_title__[^"]*">)[\s\S]*?(<\/h1>)/i,title);
  html=replaceText(html,/(<li aria-current="page">)[\s\S]*?(<\/li>)/i,llmJson.breadcrumb_blurb || description);
  // Under the headline: a short standfirst, worded differently from the
  // intro, so the opening paragraph is never shown twice.
  html=replaceText(html,/(<p class="Story_byline__(?!83)[^"]*">)[\s\S]*?(<\/p>)/i,llmJson.standfirst || description);
  html=html.replace(
    /(<p class="Story_byline__83[^"]*">)[\s\S]*?(<\/p>)/i,
    (_,open,close)=> `${open}${htmlTextEscape(date)}<!-- --> |<!-- --> <!-- -->${htmlTextEscape(product)}${close}`
  );
  html=replaceText(html,/(<p class="subscribetext">)[\s\S]*?(<\/p>)/i,'Subscribe to our newsletters');
  html=replaceAttr(html,/(<meta name="description" content=")[^"]*(")/i,description);
  html=replaceAttr(html,/(<meta name="keywords" content=")[^"]*(")/i,llmJson.meta_keywords || `${keyword}, ${product} reviews`);
  html=replaceAttr(html,/(<meta name="twitter:title" content=")[^"]*(")/i,title);
  html=replaceAttr(html,/(<meta property="og:title" content=")[^"]*(")/i,title);
  html=replaceAttr(html,/(<meta name="twitter:description" content=")[^"]*(")/i,llmJson.twitter_description_head || description);
  html=replaceAttr(html,/(<meta property="og:description" content=")[^"]*(")/i,llmJson.og_description_head || description);
  html=replaceAttr(html,/(<meta property="og:url" content=")[^"]*(")/i,canonical);
  html=html.replace(/<link rel="canonical"\s*\/>/i,`<link rel="canonical" href="${htmlAttrEscape(canonical)}"/>`);

  let cardIndex=0;
  html=html.replace(
    /(<p class="Card_title__[^"]*">)[\s\S]*?(<\/p>)/g,
    (_,open,close)=> `${open}${htmlTextEscape(llmJson.card_titles?.[cardIndex++ % 6] || title)}${close}`
  );
  cardIndex=0;
  html=html.replace(
    /(<p class="Card_excerpt__[^"]*">)[\s\S]*?(<\/p>)/g,
    (_,open,close)=> `${open}${htmlTextEscape(llmJson.card_contents?.[cardIndex++ % 6] || description)}${close}`
  );
  html=html.replace(
    /(<div class="Prose_body__[^"]*">[\s\S]*?<input[^>]*name="video_id"[^>]*\/>)([\s\S]*?)(<\/div>)/i,
    // The article: intro paragraphs, then the body sections.
    (_,open,_body,close)=> {
      const introParas=llmJson.article?.intro?.length ? llmJson.article.intro : [llmJson.main_video_description || description];
      const intro=introParas.map((t)=> `<p>${htmlTextEscape(t)}</p>`).join('');
      const evaluation=buildEvaluationHtml(llmJson.article,product,{...(llmJson.research || {}),rng});
      return `${open}${intro}${evaluation || `<p>${htmlTextEscape(llmJson.og_description_head || description)}</p>`}${close}`;
    }
  );
  html=replaceAttr(html,/(<input[^>]*name="video_title"[^>]*value=")[^"]*(")/i,title);
  // The main video's title attribute carries the template's old title + code.
  html=replaceAttr(html,/(<video\b[^>]*id="main-video"[^>]*\btitle=")[^"]*(")/i,title);
  html=html.replace(/(<strong>Product<\/strong><span>)[^<]*(<\/span>)/i,`$1${htmlTextEscape(product)}$2`);
  // The menu link "Ingredients" comes from the supplement template. Keep it
  // only when research shows the product really is a supplement/health
  // product; otherwise call it "Features".
  const category=`${llmJson.research?.factSheet?.category || ''} ${llmJson.research?.factSheet?.primary_use_case || ''}`;
  const isSupplement=/supplement|vitamin|capsule|gumm|formula|nutrition|health|wellness|diet|weight loss/i.test(category);
  if (!isSupplement) html=html.replace(/(<li>\s*<a [^>]*>)\s*Ingredients\s*(<\/a>\s*<\/li>)/gi,'$1Features$2');

  // Template filler that means nothing to a reader: the kicker badge + "ref"
  // code, "Watch ID" code, the "Region" fact (always "Not specified" after
  // geo removal) and the footer "Build ... / chunk ..." line.
  html=html.replace(/<div class="Story_kicker__[^"]*">[\s\S]*?<\/div>\s*/i,'');
  html=html.replace(/<span>\s*Watch ID [A-Z0-9]+\s*<\/span>\s*/gi,'');
  html=html.replace(/<div class="Facts_item__[^"]*"><strong>Region<\/strong><span>[^<]*<\/span><\/div>\s*/i,'');
  html=html.replace(/<p>\s*Build [A-Z0-9]+\s*\/\s*chunk [A-Z0-9]+\s*<\/p>\s*/gi,'');
  html=html.replace(/(<strong>Updated<\/strong><span>)[^<]*(<\/span>)/i,`$1${date}$2`);
  // (runs after the Product/Updated values are filled in)
  // Quick facts read "ProductSEO NEO" when the template CSS isn't loaded:
  // add a colon and a space between the label and the value.
  html=html.replace(/(<div class="Facts_item__[^"]*"><strong>)([^<:]+?)(<\/strong>)\s*(<span>)/g,'$1$2:$3 $4');
  html=html.replace(/(<p>Copyright )[^<]*(<\/p>)/i,`$1${new Date().getFullYear()} ${htmlTextEscape(product)}. All Rights Reserved.$2`);

  html=rewriteStructuredData(html,keyword,product,llmJson,canonical,videoUrl,imageUrl);
  html=html.replace(
    /(<script id="__NEXT_DATA__" type="application\/json">)([\s\S]*?)(<\/script>)/i,
    (whole,open,body,close)=> {
      try {
        const data=JSON.parse(body);
        data.props.pageProps.product=product;
        data.props.pageProps.keyword=keyword;
        data.props.pageProps.base=llmJson.subtitle || keyword;
        data.props.pageProps.stamp=date;
        data.props.pageProps.offer=title;
        return open + JSON.stringify(data) + close;
      } catch {
        return whole;
      }
    }
  );

  // The source's MP4 host differs from the older template's VIDEO_HOST.
  // Replace MP4 references independently of canonical URL rewriting.
  const videoUrls=new Map();
  html=html.replace(/https?:\/\/[^\s"'\\<>]+\.mp4/gi,(url)=> {
    if (!videoUrls.has(url)) videoUrls.set(url,videoUrl);
    return videoUrls.get(url);
  });
  html=html.replace(/https?:\\\/\\\/[^"\\\s]+\.mp4/gi,()=> videoUrl.replace(/\//g,'\\/'));
  html=html.replace(/https:\/\/pic\.ijmss\.org\/image\/[a-z0-9]+\.jpg/gi,()=> (
    randImageUrl(rng.choice(IMAGE_HOSTS),rng,customImageBase)
  ));

  // Replace source campaign text in remaining static nodes and escaped
  // hydration strings without parsing or restructuring the Next.js payload.
  html=html.replace(/MITOLYN Reviews MITOLYN [^<"\\]+/gi,()=> htmlTextEscape(llmJson.meta_description || description));   // hidden copies get the meta description, not the full intro
  // Handles like @mitolyn can't contain spaces, so use the compact name.
  html=html.replace(/@mitolyn/gi,`@${slugify(product).replace(/-/g,'')}`);
  html=html.replace(/mitolyn/gi,product);
  const base=scriptBase?.trim() || 'https://example.com/js/';
  const scriptUrl=`${base.endsWith('/') ? base : `${base}/`}${slugify(scriptName || product).replace(/-/g,'')}.js`;
  html=html.replace('</head>',`<script src="${htmlAttrEscape(scriptUrl)}" id="query" value="query"></script>\n</head>`);
  changes.push('[story] Replaced campaign headings, metadata, body copy, cards and structured data');
  changes.push(`[video] Routed MP4 references through ${customVideoBase || VIDEO_HOST}`);
  changes.push(`[canonical] Set page metadata to ${canonical}`);
  return {html,changes};
};